import * as pdfjsLib from "pdfjs-dist/build/pdf";
import mammoth from "mammoth";

// IMPORTANT: Setup worker for pdf.js
// Copy `pdf.worker.min.js` from `node_modules/pdfjs-dist/build/` to your `public` folder
// Or use a CDN:
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
// If you copied it to public folder:
// pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

export const extractTextFromPdf = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let textContent = "";
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const text = await page.getTextContent();
    textContent += text.items.map((item) => item.str).join(" ") + "\n";
  }
  return textContent;
};

export const extractTextFromDocx = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
};

// Basic .doc to text (less reliable, recommend .docx or PDF)
// This is a very simple attempt and might not work well for complex .doc files.
// For robust .doc parsing, a server-side solution or more complex library is often needed.
export const extractTextFromDoc = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        // Mammoth doesn't directly support .doc, so this is a placeholder.
        // For a real solution, you might try to convert .doc to .docx first (server-side)
        // or use a library that attempts .doc parsing, though client-side options are limited.
        // For now, we'll just inform the user.
        // A simple approach: try to read as text, might get some readable content for simple docs.
        const text = event.target.result;
        // A very naive check if it might be plain text or something parsable
        if (text.length > 0 && text.length < 5 * 1024 * 1024) {
          // Max 5MB
          // Try to remove common non-printable characters, this is very basic
          const cleanedText = text.replace(/[^\x20-\x7E\s]/g, "");
          if (cleanedText.length > 100) {
            // Arbitrary threshold for "some content"
            resolve(
              "(.doc file) Partially extracted text: \n" +
                cleanedText.substring(0, 5000) +
                "\n... (extraction might be incomplete or garbled)"
            );
          } else {
            reject(
              new Error(
                ".doc file content could not be reliably extracted client-side. Please use .docx or .pdf."
              )
            );
          }
        } else {
          reject(
            new Error(
              ".doc file is too large or not text-based for simple client-side extraction. Please use .docx or .pdf."
            )
          );
        }
      } catch (err) {
        reject(new Error("Could not process .doc file. " + err.message));
      }
    };
    reader.onerror = (err) => reject(new Error("Error reading .doc file."));
    // Try reading as text. For binary .doc, this won't be very useful.
    reader.readAsText(file);
  });
};

export const downloadInterviewReport = (userResponses, overallFeedbackText) => {
  if (!userResponses || userResponses.length === 0) {
    alert("No interview data available to download.");
    return;
  }

  let content = `# Mock Interview Report\n`;
  content += `Generated: ${new Date().toLocaleDateString()}\n\n`;

  if (overallFeedbackText) {
    content += `## Overall Feedback\n${overallFeedbackText}\n\n`;
  }

  content += `## Detailed Breakdown\n\n`;
  userResponses.forEach((response, index) => {
    content += `### Question ${index + 1}: ${response.question?.question || "N/A"}\n`;
    content += `- **Your Answer:** ${response.answerText || "N/A"}\n`;
    if (response.feedback?.overallScore !== undefined) {
      content += `- **Score:** ${response.feedback.overallScore}%\n`;
    }
    if (response.feedback?.overallAssessment) {
      content += `- **Feedback:** ${response.feedback.overallAssessment}\n`;
    }
    content += `\n`;
  });

  const blob = new Blob([content], { type: "text/markdown;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `Interview_Report_${new Date().toISOString().slice(0, 10)}.md`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
